/**
 * 固件升级相关类型和工具函数
 * 包含固件升级的数据结构、CRC 校验、数据包构造等
 */

export type { Firmware } from '@/core/ota'
export {
  getSendFileLength,
  buildStartPacket,
  buildDataPacketFromFirmware,
  buildEndPacket,
  advanceFirmware,
  OTA_CHUNK_SIZE,
} from '@/core/ota'

export type UpgradeTarget = 'dongle' | 'headset' | 'bluetooth'
export type UpgradeState = 'idle' | 'checking' | 'preparing' | 'upgrading' | 'completed' | 'error'

/**
 * 固件信息接口
 */
export interface FirmwareInfo {
  version: string
  fileName: string
  size: number
  releaseDate?: string
  releaseNotes?: string
  downloadUrl?: string
}

/**
 * 升级设备状态接口
 */
export interface UpgradeDeviceStatus {
  currentVersion: string
  targetVersion: string
  deviceType: UpgradeTarget
  batteryLevel: number
  isCharging: boolean
  canUpgrade: boolean
  reason?: string // 如果不能升级的原因
}

export interface UpgradeProgress {
  state: UpgradeState
  progress: number // 0-100
  currentBytes: number
  totalBytes: number
  speed?: string // KB/s
  estimatedTime?: number // seconds
}

export interface UpgradeCallbacks {
  onProgress?: (progress: UpgradeProgress) => void
  onComplete?: () => void
  onError?: (error: Error) => void
  onLog?: (message: string) => void
}

// CRC16-CCITT 计算
export function crc16Ccitt(data: Uint8Array): number {
  const POLY = 0x1021
  let crc = 0xFFFF

  for (let i = 0; i < data.length; i++) {
    crc ^= (data[i] << 8)
    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) {
        crc = (crc << 1) ^ POLY
      } else {
        crc <<= 1
      }
      crc &= 0xFFFF
    }
  }

  return crc
}

// 构建握手包
export function buildHandshakePacket(_reportId = 0): Uint8Array {
  const packet = new Uint8Array(64)
  packet[0] = 0xAA
  packet[1] = 0x55
  packet[4] = 0x11 // 握手命令

  // CRC校验
  const crc = crc16Ccitt(packet.slice(0, 6))
  packet[6] = (crc >> 8) & 0xFF
  packet[7] = crc & 0xFF

  return packet
}

// 构建数据包
export function buildDataPacket(
  sequence: number,
  data: Uint8Array,
  offset: number,
  chunkSize: number = 56
): Uint8Array {
  const packet = new Uint8Array(64)
  packet[0] = 0xAA
  packet[1] = 0x55
  packet[2] = (sequence >> 8) & 0xFF
  packet[3] = sequence & 0xFF
  packet[4] = 0x81 // 数据传输命令

  // 数据区从第8字节开始，最多56字节
  const dataLen = Math.min(chunkSize, data.length - offset)
  for (let i = 0; i < dataLen; i++) {
    packet[8 + i] = data[offset + i]
  }

  // CRC校验
  const crc = crc16Ccitt(packet.slice(0, 8 + dataLen))
  packet[6] = (crc >> 8) & 0xFF
  packet[7] = crc & 0xFF

  return packet
}

// 解析设备响应
export function parseDeviceResponse(data: Uint8Array): {
  isValid: boolean
  command: number
  data: Uint8Array
} {
  if (data.length < 8) {
    return { isValid: false, command: 0, data: new Uint8Array() }
  }

  // 验证头
  if (data[0] !== 0xAA || data[1] !== 0x55) {
    return { isValid: false, command: 0, data: new Uint8Array() }
  }

  // 验证CRC
  const receivedCrc = (data[6] << 8) | data[7]
  const calculatedCrc = crc16Ccitt(data.slice(0, 6))
  if (receivedCrc !== calculatedCrc) {
    return { isValid: false, command: 0, data: new Uint8Array() }
  }

  return {
    isValid: true,
    command: data[4],
    data: data.slice(8),
  }
}

// 计算升级预估时间
export function estimateUpgradeTime(fileSize: number, speedKbps: number = 8): number {
  return Math.ceil(fileSize / (speedKbps * 1024))
}

// 格式化字节大小
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

// 格式化时间
export function formatTime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
}
